// @ts-nocheck
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  UserCircle, Mail, Calendar, Star, List, Heart, PlusCircle, Loader2,
  Edit, Home, Share2, Twitter, Facebook, MessageSquare
} from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import { UserProfile as UserProfileType, Video, VideoList } from '../types';
import VideoCard from './VideoCard';

interface UserProfileProps {
  user: UserProfileType;
  onVideoClick: (video: Video) => void;
  onGoHome: () => void;
}

const UserProfile: React.FC<UserProfileProps> = ({ user, onVideoClick, onGoHome }) => {
  const [userVideos, setUserVideos] = useState<Video[]>([]);
  const [favoriteVideos, setFavoriteVideos] = useState<Video[]>([]);
  const [userLists, setUserLists] = useState<VideoList[]>([]);
  const [loadingContent, setLoadingContent] = useState(true);
  const [activeTab, setActiveTab] = useState<'myVideos' | 'favorites' | 'myLists'>('myVideos');
  const [showShareOptions, setShowShareOptions] = useState<string | null>(null);
  const shareMenuRef = useRef<HTMLDivElement | null>(null);

  // Close share menu on outside click / Esc
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (showShareOptions && shareMenuRef.current && !shareMenuRef.current.contains(e.target as Node)) {
        setShowShareOptions(null);
      }
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setShowShareOptions(null);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [showShareOptions]);

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'N/A';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return 'N/A';
    return d.toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });
    // If you want MX format specifically: 'es-MX'
  };

  const getShareableLink = (listId: string) => {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}/list/${listId}`;
  };

  const fetchUserContent = useCallback(async () => {
    if (!user?.id) return;
    setLoadingContent(true);
    try {
      // Videos by user
      const { data: videosData, error: videosError } = await supabase
        .from('videos')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (videosError) {
        console.error('Error fetching user videos:', videosError);
      } else {
        setUserVideos((videosData || []) as Video[]);
      }

      // Favorites (placeholder: sample selection). Replace with your join when ready.
      const { data: favsData, error: favsError } = await supabase
        .from('videos')
        .select('*')
        .limit(6);

      if (favsError) {
        console.error('Error fetching favorite videos:', favsError);
      } else {
        setFavoriteVideos((favsData || []) as Video[]);
      }

      // Lists owned by user
      const { data: listsData, error: listsError } = await supabase
        .from('video_lists')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (listsError) {
        console.error('Error fetching user lists:', listsError);
      } else {
        setUserLists((listsData || []) as VideoList[]);
      }
    } finally {
      setLoadingContent(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchUserContent();
  }, [fetchUserContent]);

  const handleShareList = (listId: string) => {
    setShowShareOptions(prev => (prev === listId ? null : listId));
  };

  const shareOnTwitter = (listId: string, listName: string) => {
    const link = getShareableLink(listId);
    const text = encodeURIComponent(`¡Mira mi lista de videos de comida "${listName}" en FoodSpotter!`);
    const url = `https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(link)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    setShowShareOptions(null);
  };

  const shareOnFacebook = (listId: string) => {
    const link = getShareableLink(listId);
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    setShowShareOptions(null);
  };

  const shareOnWhatsApp = (listId: string, listName: string) => {
    const link = getShareableLink(listId);
    const text = encodeURIComponent(`¡Mira mi lista de videos de comida "${listName}" en FoodSpotter! ${link}`);
    const url = `https://api.whatsapp.com/send?text=${text}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    setShowShareOptions(null);
  };

  const copyLinkToClipboard = async (listId: string) => {
    try {
      const link = getShareableLink(listId);
      await navigator.clipboard.writeText(link);
      alert('¡Enlace copiado al portapapeles!');
    } catch {
      // Fallback
      const link = getShareableLink(listId);
      const ta = document.createElement('textarea');
      ta.value = link;
      document.body.appendChild(ta);
      ta.select(); document.execCommand('copy');
      document.body.removeChild(ta);
      alert('¡Enlace copiado!');
    } finally {
      setShowShareOptions(null);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-3xl p-8 shadow-xl max-w-4xl mx-auto my-12"
    >
      <div className="flex justify-between items-center mb-8 border-b pb-6 border-gray-200">
        <div className="flex items-center gap-6">
          <UserCircle className="w-24 h-24 text-blue-500" />
          <div>
            <h2 className="text-4xl font-bold text-gray-900 break-words">{user.email || 'Usuario'}</h2>
            <p className="text-gray-600 text-lg flex items-center gap-2 mt-2">
              <Mail className="w-5 h-5" /> {user.email || 'N/A'}
            </p>
            <p className="text-gray-600 text-sm flex items-center gap-2 mt-1">
              <Calendar className="w-4 h-4" /> Miembro desde: {formatDate(user.created_at)}
            </p>
            <p className="text-gray-600 text-sm flex items-center gap-2 mt-1">
              <Star className="w-4 h-4 text-yellow-500 fill-current" />
              Suscripción: {user.is_subscribed ? 'Premium' : 'Gratis'}
              {user.trial_ends_at && !user.is_subscribed ? (
                <span className="ml-2 text-blue-500">(Prueba hasta: {formatDate(user.trial_ends_at)})</span>
              ) : null}
            </p>
            <p className="text-gray-600 text-sm flex items-center gap-2 mt-1">
              <List className="w-4 h-4" /> Extracciones realizadas: {user.extraction_count ?? 0}
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <motion.button
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl font-semibold flex items-center gap-2 hover:bg-gray-200 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => alert('Funcionalidad de edición de perfil no implementada aún.')}
          >
            <Edit className="w-4 h-4" />
            Editar Perfil
          </motion.button>
          <motion.button
            className="px-4 py-2 bg-blue-500 text-white rounded-xl font-semibold flex items-center gap-2 hover:bg-blue-600 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onGoHome}
          >
            <Home className="w-4 h-4" />
            Volver al Inicio
          </motion.button>
        </div>
      </div>

      <div className="flex border-b border-gray-200 mb-6">
        <button
          className={`flex-1 py-3 text-lg font-semibold transition-colors ${
            activeTab === 'myVideos' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-gray-800'
          }`}
          onClick={() => setActiveTab('myVideos')}
        >
          Mis Videos ({userVideos.length})
        </button>
        <button
          className={`flex-1 py-3 text-lg font-semibold transition-colors ${
            activeTab === 'favorites' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-gray-800'
          }`}
          onClick={() => setActiveTab('favorites')}
        >
          Favoritos ({favoriteVideos.length})
        </button>
        <button
          className={`flex-1 py-3 text-lg font-semibold transition-colors ${
            activeTab === 'myLists' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-gray-800'
          }`}
          onClick={() => setActiveTab('myLists')}
        >
          Mis Listas ({userLists.length})
        </button>
      </div>

      {loadingContent ? (
        <div className="flex justify-center items-center h-48">
          <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
        </div>
      ) : (
        <div>
          {activeTab === 'myVideos' && (
            userVideos.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {userVideos.map((video) => (
                  <VideoCard key={video.id} video={video} onVideoClick={onVideoClick} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <PlusCircle className="w-16 h-16 mx-auto mb-4" />
                <p className="text-xl font-medium">Aún no has añadido ningún video.</p>
                <p className="mb-6">¡Empieza a explorar y añade tus descubrimientos!</p>
                <div className="text-sm">
                  <p className="mb-3 text-gray-600">Try one of these sample clips:</p>
                  <div className="flex flex-wrap justify-center gap-2 text-xs">
                    <span className="px-3 py-1 bg-pink-50 text-pink-700 rounded-full">📱 @foodie.mx Instagram</span>
                    <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full">🎵 TikTok food tours</span>
                    <span className="px-3 py-1 bg-red-50 text-red-700 rounded-full">📺 YouTube Shorts</span>
                  </div>
                </div>
              </div>
            )
          )}

          {activeTab === 'favorites' && (
            favoriteVideos.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {favoriteVideos.map((video) => (
                  <VideoCard key={video.id} video={video} onVideoClick={onVideoClick} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Heart className="w-16 h-16 mx-auto mb-4" />
                <p className="text-xl font-medium">Aún no tienes videos favoritos.</p>
                <p className="mb-6">Marca tus videos preferidos para verlos aquí.</p>
                <div className="text-sm">
                  <p className="mb-3 text-gray-600">Popular spots to save:</p>
                  <div className="flex flex-wrap justify-center gap-2 text-xs">
                    <span className="px-3 py-1 bg-yellow-50 text-yellow-700 rounded-full">🌮 Taco spots</span>
                    <span className="px-3 py-1 bg-green-50 text-green-700 rounded-full">🍜 Ramen bars</span>
                    <span className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full">🍰 Dessert places</span>
                  </div>
                </div>
              </div>
            )
          )}

          {activeTab === 'myLists' && (
            userLists.length > 0 ? (
              <div className="space-y-6">
                {userLists.map((list) => (
                  <motion.div
                    key={list.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gray-50 border border-gray-200 rounded-2xl p-5 flex items-center justify-between relative"
                  >
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{list.name}</h3>
                      <p className="text-gray-600 text-sm">{list.description || 'Sin descripción'}</p>
                      <p className="text-gray-500 text-xs mt-1">Creada el: {formatDate(list.created_at)}</p>
                    </div>
                    <div className="flex gap-3">
                      <motion.button
                        className="px-4 py-2 bg-blue-500 text-white rounded-xl font-semibold flex items-center gap-2 hover:bg-blue-600 transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => alert(`Ver videos de la lista: ${list.name}`)}
                      >
                        <List className="w-4 h-4" />
                        Ver Lista
                      </motion.button>
                      <motion.button
                        className="px-4 py-2 bg-green-500 text-white rounded-xl font-semibold flex items-center gap-2 hover:bg-green-600 transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleShareList(list.id)}
                        aria-expanded={showShareOptions === list.id}
                        aria-haspopup="menu"
                      >
                        <Share2 className="w-4 h-4" />
                        Compartir
                      </motion.button>

                      {showShareOptions === list.id && (
                        <motion.div
                          ref={shareMenuRef}
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg p-2 z-10 border border-gray-200"
                          role="menu"
                        >
                          <button
                            className="w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md flex items-center gap-2"
                            onClick={() => shareOnTwitter(list.id, list.name)}
                          >
                            <Twitter className="w-4 h-4 text-blue-400" /> Compartir en Twitter
                          </button>
                          <button
                            className="w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md flex items-center gap-2"
                            onClick={() => shareOnFacebook(list.id)}
                          >
                            <Facebook className="w-4 h-4 text-blue-600" /> Compartir en Facebook
                          </button>
                          <button
                            className="w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md flex items-center gap-2"
                            onClick={() => shareOnWhatsApp(list.id, list.name)}
                          >
                            <MessageSquare className="w-4 h-4 text-green-500" /> Compartir en WhatsApp
                          </button>
                          <button
                            className="w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md flex items-center gap-2"
                            onClick={() => copyLinkToClipboard(list.id)}
                          >
                            <Share2 className="w-4 h-4 text-gray-500" /> Copiar Enlace
                          </button>
                        </motion.div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <List className="w-16 h-16 mx-auto mb-4" />
                <p className="text-xl font-medium">Aún no has creado ninguna lista.</p>
                <p>¡Organiza tus videos en listas personalizadas!</p>
                <motion.button
                  className="mt-6 px-6 py-3 bg-blue-500 text-white rounded-xl font-semibold flex items-center justify-center mx-auto gap-2 hover:bg-blue-600 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => alert('Funcionalidad de creación de listas no implementada aún.')}
                >
                  <PlusCircle className="w-5 h-5" />
                  Crear Nueva Lista
                </motion.button>
              </div>
            )
          )}
        </div>
      )}
    </motion.div>
  );
};

export default UserProfile;
